/**
 * Language building blocks for every indexable page, in all five languages.
 *
 * The prerenderer and the React pages both build their copy from this module
 * (through projectSeo.js, areas.js, developers.js and pageCopy.js), so the
 * static HTML Google fetches and the DOM it renders say the same thing in the
 * same language. Plain JS, no React, no browser APIs.
 */

const CITY = {
    Dubai: { ar: 'دبي', en: 'Dubai', ru: 'Дубай', zh: '迪拜', ur: 'دبئی' },
    'Abu Dhabi': { ar: 'أبوظبي', en: 'Abu Dhabi', ru: 'Абу-Даби', zh: '阿布扎比', ur: 'ابوظہبی' },
    'Ras Al Khaimah': { ar: 'رأس الخيمة', en: 'Ras Al Khaimah', ru: 'Рас-эль-Хайма', zh: '哈伊马角', ur: 'راس الخیمہ' },
    Sharjah: { ar: 'الشارقة', en: 'Sharjah', ru: 'Шарджа', zh: '沙迦', ur: 'شارجہ' },
    Ajman: { ar: 'عجمان', en: 'Ajman', ru: 'Аджман', zh: '阿治曼', ur: 'عجمان' },
    'Umm Al Quwain': { ar: 'أم القيوين', en: 'Umm Al Quwain', ru: 'Умм-эль-Кайвайн', zh: '乌姆盖万', ur: 'ام القیوین' }
};
export const cityName = (city, lang) => CITY[city]?.[lang] || city || CITY.Dubai[lang];

/**
 * A district as a reader should see it. The feed's names carry their
 * disambiguation in brackets and stray double spaces — "JVC (Jumeirah
 * Village Circle)", "Meydan (Nad Al Sheba  1)" — which read as raw data in a
 * title or a sentence.
 */
export const districtLabel = (d) =>
    String(d || '').replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * A URL slug: "Object One" → "object-one", "JVC (Jumeirah Village Circle)"
 * → "jvc". Names with nothing Latin in them fall back to a short stable hash
 * rather than an empty slug.
 */
export function slugify(name) {
    const s = districtLabel(name)
        .normalize('NFKD').replace(/[̀-ͯ]/g, '')
        .toLowerCase().replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    if (s) return s;
    let h = 0;
    for (const ch of String(name || '')) h = (h * 31 + ch.codePointAt(0)) >>> 0;
    return `x${h.toString(36)}`;
}

export const UAE = { ar: 'الإمارات', en: 'UAE', ru: 'ОАЭ', zh: '阿联酋', ur: 'متحدہ عرب امارات' };

/** Unit categories as the feed spells them, singular or plural. */
const CATEGORY = {
    apartment: { ar: 'شقق', en: 'apartments', ru: 'апартаменты', zh: '公寓', ur: 'اپارٹمنٹس' },
    villa: { ar: 'فلل', en: 'villas', ru: 'виллы', zh: '别墅', ur: 'ولاز' },
    townhouse: { ar: 'تاون هاوس', en: 'townhouses', ru: 'таунхаусы', zh: '联排别墅', ur: 'ٹاؤن ہاؤسز' },
    penthouse: { ar: 'بنتهاوس', en: 'penthouses', ru: 'пентхаусы', zh: '顶层公寓', ur: 'پینٹ ہاؤسز' },
    duplex: { ar: 'دوبلكس', en: 'duplexes', ru: 'дуплексы', zh: '复式公寓', ur: 'ڈوپلیکس' },
    office: { ar: 'مكاتب', en: 'offices', ru: 'офисы', zh: '办公室', ur: 'دفاتر' },
    retail: { ar: 'محلات تجارية', en: 'retail units', ru: 'торговые помещения', zh: '商铺', ur: 'دکانیں' },
    plot: { ar: 'أراضٍ', en: 'plots', ru: 'участки', zh: '地块', ur: 'پلاٹس' },
    hotelapartment: { ar: 'شقق فندقية', en: 'hotel apartments', ru: 'гостиничные апартаменты', zh: '酒店式公寓', ur: 'ہوٹل اپارٹمنٹس' },
    mansion: { ar: 'قصور', en: 'mansions', ru: 'особняки', zh: '豪宅', ur: 'مینشنز' }
};
const RESIDENTIAL = { ar: 'وحدات سكنية', en: 'residential units', ru: 'жилые объекты', zh: '住宅单位', ur: 'رہائشی یونٹس' };
export function categoryName(c, lang) {
    const key = String(c || '').toLowerCase().replace(/\s+/g, '').replace(/s$/, '');
    return CATEGORY[key]?.[lang] || RESIDENTIAL[lang];
}

/** 1,862,313 → "1.86M" style, per language. Long digit strings eat titles. */
export function compactPrice(value, lang = 'en') {
    const n = Number(value);
    if (!isFinite(n) || n <= 0) return null;
    // "1.80" → "1.8", "2.00" → "2", "12.50" → "12.5"; never "10" → "1".
    const m = (x) => (x / 1e6).toFixed(x >= 1e7 ? 1 : 2).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
    if (lang === 'ar') {
        if (n >= 1e6) return `${m(n)} مليون درهم`;
        if (n >= 1e3) return `${Math.round(n / 1e3)} ألف درهم`;
        return `${n} درهم`;
    }
    if (lang === 'ru') {
        if (n >= 1e6) return `${m(n).replace('.', ',')} млн AED`;
        if (n >= 1e3) return `${Math.round(n / 1e3)} тыс. AED`;
        return `${n} AED`;
    }
    if (lang === 'zh') {
        // 万 (10,000) is how Chinese readers scale prices.
        if (n >= 1e4) return `${(n / 1e4).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, '')}万迪拉姆`;
        return `${n}迪拉姆`;
    }
    if (n >= 1e6) return `AED ${m(n)}M`;
    if (n >= 1e3) return `AED ${Math.round(n / 1e3)}K`;
    return `AED ${n}`;
}

/** Full form with separators — for body copy, where space is not scarce. */
export function fullPrice(value, lang = 'en') {
    const n = Number(value);
    if (!isFinite(n) || n <= 0) return null;
    const digits = n.toLocaleString('en-US');
    if (lang === 'ar') return `${digits} درهم`;
    if (lang === 'zh') return `${digits} 迪拉姆`;
    if (lang === 'ur') return `${digits} درہم`;
    return `AED ${digits}`;
}

const QUARTER_AR = { 1: 'الربع الأول', 2: 'الربع الثاني', 3: 'الربع الثالث', 4: 'الربع الرابع' };

/**
 * "2025-12-31" → "Q4 2025" in each language. `past` flags a date already
 * behind us: a 2022 handover is a completed project, not an expectation.
 */
export function parseHandover(date) {
    if (!date) return null;
    const d = new Date(String(date).slice(0, 10));
    if (isNaN(d)) return null;
    const q = Math.floor(d.getUTCMonth() / 3) + 1;
    const y = d.getUTCFullYear();
    return {
        past: d < new Date(),
        year: y,
        quarter: q,
        label: {
            ar: `${QUARTER_AR[q]} ${y}`,
            en: `Q${q} ${y}`,
            ru: `${q} кв. ${y}`,
            zh: `${y}年第${q}季度`,
            ur: `${y} کی سہ ماہی ${q}`
        }
    };
}
export const handoverLabel = (date, lang = 'en') => parseHandover(date)?.label[lang] || null;
export const isHandoverPast = (date) => parseHandover(date)?.past ?? false;

/** Russian plural: 1 проект, 2 проекта, 5 проектов. */
function ruPlural(n, [one, few, many]) {
    const k = n % 100;
    const l = n % 10;
    if (k >= 11 && k <= 14) return many;
    if (l === 1) return one;
    if (l >= 2 && l <= 4) return few;
    return many;
}

/** "35 مشروعاً" / "35 projects" / "35 проектов" / "35个项目" / "35 پروجیکٹس". */
export function projectCount(n, lang) {
    if (lang === 'ar') {
        const k = n % 100;
        if (n === 1) return 'مشروع واحد';
        if (n === 2) return 'مشروعان';
        if (k >= 3 && k <= 10) return `${n} مشاريع`;
        if (k >= 11 && k <= 99) return `${n} مشروعاً`;
        return `${n} مشروع`;
    }
    if (lang === 'ru') return `${n} ${ruPlural(n, ['проект', 'проекта', 'проектов'])}`;
    if (lang === 'zh') return `${n}个项目`;
    if (lang === 'ur') return `${n} ${n === 1 ? 'پروجیکٹ' : 'پروجیکٹس'}`;
    return `${n} ${n === 1 ? 'project' : 'projects'}`;
}

/** "A، B و C" / "A, B and C" / "A、B和C". */
export function listJoin(items, lang) {
    const xs = items.filter(Boolean);
    if (xs.length < 2) return xs.join('');
    const last = xs[xs.length - 1];
    const head = xs.slice(0, -1);
    switch (lang) {
        case 'ar': return `${head.join('، ')} و${last}`;
        case 'ur': return `${head.join('، ')} اور ${last}`;
        case 'ru': return `${head.join(', ')} и ${last}`;
        case 'zh': return `${head.join('、')}和${last}`;
        default: return `${head.join(', ')} and ${last}`;
    }
}

/** Cuts on a word boundary and never mid-clause punctuation. */
export function truncate(s, max) {
    if (s.length <= max) return s;
    const cut = s.slice(0, max);
    const i = Math.max(cut.lastIndexOf(' '), cut.lastIndexOf('，'), cut.lastIndexOf('、'));
    return (i > max * 0.6 ? cut.slice(0, i) : cut).replace(/[،,.，、\s]+$/, '') + '…';
}

/**
 * Interface labels shared by the static shell and the page components.
 */
export const UI = {
    ar: {
        home: 'الرئيسية', projects: 'كل المشاريع', areas: 'المناطق', developers: 'المطوّرون', faq: 'الأسئلة الشائعة',
        blog: 'أدلّة الشراء', about: 'من نحن', contact: 'تواصل معنا', approved: 'العقارات المعتمدة',
        ai: 'المستشار الذكي', privacy: 'سياسة الخصوصية', terms: 'الشروط والأحكام', resell: 'اعرض عقارك للبيع',
        crumbs: 'مسار التنقّل', siteNav: 'روابط الموقع', otherLanguages: 'لغات أخرى',
        licence: (n) => `وسيط عقاري مرخّص من دائرة الأراضي والأملاك بدبي — رقم ${n}`,
        sqft: 'قدم مربع', from: 'يبدأ من', soldOut: 'مباع بالكامل', handover: 'التسليم'
    },
    en: {
        home: 'Home', projects: 'All projects', areas: 'Areas', developers: 'Developers', faq: 'FAQ',
        blog: 'Buying guides', about: 'About us', contact: 'Contact us', approved: 'Approved listings',
        ai: 'AI advisor', privacy: 'Privacy policy', terms: 'Terms', resell: 'Sell your property',
        crumbs: 'Breadcrumb', siteNav: 'Site links', otherLanguages: 'Other languages',
        licence: (n) => `Licensed real estate broker, Dubai Land Department — No. ${n}`,
        sqft: 'sq ft', from: 'from', soldOut: 'sold out', handover: 'handover'
    },
    ru: {
        home: 'Главная', projects: 'Все проекты', areas: 'Районы', developers: 'Застройщики', faq: 'Вопросы и ответы',
        blog: 'Гиды по покупке', about: 'О нас', contact: 'Контакты', approved: 'Проверенные объекты',
        ai: 'ИИ-консультант', privacy: 'Конфиденциальность', terms: 'Условия', resell: 'Продать объект',
        crumbs: 'Навигация', siteNav: 'Разделы сайта', otherLanguages: 'Другие языки',
        licence: (n) => `Лицензированный брокер Земельного департамента Дубая — № ${n}`,
        sqft: 'кв. футов', from: 'от', soldOut: 'распродано', handover: 'сдача'
    },
    zh: {
        home: '首页', projects: '全部项目', areas: '区域', developers: '开发商', faq: '常见问题',
        blog: '购房指南', about: '关于我们', contact: '联系我们', approved: '认证房源',
        ai: 'AI 房产顾问', privacy: '隐私政策', terms: '条款', resell: '出售房产',
        crumbs: '导航路径', siteNav: '网站链接', otherLanguages: '其他语言',
        licence: (n) => `迪拜土地局持牌房产经纪 — 编号 ${n}`,
        sqft: '平方英尺', from: '起', soldOut: '已售罄', handover: '交付'
    },
    ur: {
        home: 'ہوم', projects: 'تمام پروجیکٹس', areas: 'علاقے', developers: 'ڈیولپرز', faq: 'عمومی سوالات',
        blog: 'خریداری گائیڈز', about: 'ہمارے بارے میں', contact: 'رابطہ کریں', approved: 'منظور شدہ لسٹنگز',
        ai: 'AI ایڈوائزر', privacy: 'رازداری کی پالیسی', terms: 'شرائط', resell: 'جائیداد فروخت کریں',
        crumbs: 'نیویگیشن', siteNav: 'سائٹ کے لنکس', otherLanguages: 'دیگر زبانیں',
        licence: (n) => `دبئی لینڈ ڈیپارٹمنٹ سے لائسنس یافتہ بروکر — نمبر ${n}`,
        sqft: 'مربع فٹ', from: 'سے شروع', soldOut: 'فروخت ہو چکا', handover: 'حوالگی'
    }
};
export const ui = (lang) => UI[lang] || UI.en;
